import { DataLoanDefinitionTct } from '@dealstack/shared/lib/generated';
import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import * as Yup from 'yup';
import { LoanDefinitionType } from '@dealstack/shared/lib/generated/models/LoanDefinitionType.ts';
import { SetOptional } from 'type-fest';

export interface FormSingleLoanDefinition extends SetOptional<DataLoanDefinitionTct, 'id'> {
	isOnEdit: boolean;
	isOnAdd: boolean;
	localId: string;
	locked?: boolean;
	funderId?: string;
	funderIsNew?: boolean;
	funderName?: string;
}

export enum LoanDefinitionFormValues {
	loanDefinitions = 'loanDefinitions',
}

export interface LoanDefinitionForm {
	[LoanDefinitionFormValues.loanDefinitions]: FormSingleLoanDefinition[];
}

export const getLoanDefinitionValidationSchema = (t: TFunction) =>
	Yup.object().shape({
		loanDefinitions: Yup.array().of(
			Yup.object().shape({
				name: Yup.string()
					.required(t(`name_field_is_required`))
					.test('unique', t(`not_unique_loan_name`), (_, context) => {
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						const [rowContext, formContext] = context.from;
						const { name, type } = rowContext.value;
						const allLoanDefinitions: FormSingleLoanDefinition[] =
							formContext.value.loanDefinitions;
						return (
							allLoanDefinitions.filter(
								(loanDefinition) =>
									loanDefinition.name?.trim() === name.trim() &&
									loanDefinition.type === type
							).length <= 1
						);
					})
					.max(48, t('loan_length_limit_error')),
				funderId: Yup.string().required(t(`loan_funder_is_required`)),
			})
		),
	});

export const useLoanDefinitionTableController = (
	loanDefinitionForm: UseFormReturn<LoanDefinitionForm, unknown>
) => {
	const {
		fields: loanDefinitionFields,
		append,
		remove,
	} = useFieldArray<LoanDefinitionForm, LoanDefinitionFormValues.loanDefinitions>({
		name: LoanDefinitionFormValues.loanDefinitions,
		control: loanDefinitionForm.control,
	});

	const onRemoveLoanDefinitionRow = (id: string) => () => {
		const index = loanDefinitionFields.findIndex((item) => item.id === id);
		const field: FormSingleLoanDefinition = loanDefinitionFields[index];
		if (!field) {
			return;
		}
		remove(index);
	};

	const onAddLoanDefinitionRow = () => {
		const newLoanDefinitionRow: FormSingleLoanDefinition = {
			name: '',
			localId: generateUUID(),
			type: LoanDefinitionType.MANAGEMENT_LOAN,
			funder: '',
			funderId: '',
			isOnEdit: true,
			isOnAdd: true,
			locked: false,
		};
		append(newLoanDefinitionRow);
	};

	return { loanDefinitionFields, onAddLoanDefinitionRow, onRemoveLoanDefinitionRow };
};
